import {
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  IconButton,
  ListContext,
  ListContextValue,
  LockedIcon,
  MenuItemType,
  Stack,
  Tooltip,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { useTheme } from "@mui/material/styles";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, Permission, PermissionAction, StudentServiceTemplate } from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const StudentServiceTemplatePageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  reroute: (id: number, crudMode: CrudMode) => void;
  fetch: (id: number) => void;
  template: StudentServiceTemplate;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.template.id;
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [savingFavorite, setSavingFavorite] = useState<boolean>(false);
  const saveEntriesCount = props.template.createAdditionalEntries
    ? props.template.createAdditionalEntries.length + 1
    : 1;

  useEffect(() => {
    if (id > 0) {
      ApiService.studentServiceTemplateFavorites.get(id).then((response) => {
        setIsFavorite(response);
      });
    }
  }, [id]);

  const toggleFavorite = async () => {
    if (savingFavorite) return;

    setSavingFavorite(true);

    try {
      const nextIsFavorite = !isFavorite;
      await ApiService.studentServiceTemplateFavorites.saveFavorite(id, nextIsFavorite);

      setIsFavorite(nextIsFavorite);
      listContext.loadList?.();
    } catch {
      dispatch(setAppToast(newToast("error")));
    } finally {
      setSavingFavorite(false);
    }
  };

  const save = (template: StudentServiceTemplate) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.studentServiceTemplates
        .saveUpdate(template)
        .then(() => {
          props.reroute(id, "view");
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    } else {
      ApiService.studentServiceTemplates
        .saveCreate(template)
        .then((response) => {
          const id = response;

          if (id && id > 0) {
            props.reroute(id, "view");
            props.fetch(id);
          } else {
            navigate("../");
          }

          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.studentServiceTemplates.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (
      isAuthorized(props.rootPermission, PermissionAction.Delete) &&
      (!props.template.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock))
    )
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit">
          {id === 0 ? "New" : ""} {props.handle}
        </Typography>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {props.template.isLocked && <LockedIcon />}

        {id > 0 && <DataModelInformation color="primary" dataModelType={DataModelEnum.StudentServiceTemplate} dataModelId={id} />}

        {id > 0 && (
          <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
            <IconButton
              color="primary"
              onClick={toggleFavorite}
              disabled={savingFavorite}
              aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
            >
              {isFavorite ? <FavoriteIcon sx={{ color: theme.palette.dashboardTabs.red }} /> : <FavoriteBorderIcon />}
            </IconButton>
          </Tooltip>
        )}

        <CloseButton confirm={props.unsavedChanges} />

        {props.crudMode === "view" &&
          isAuthorized(props.rootPermission, PermissionAction.Update) &&
          (!props.template.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
            <UpdateButton onClick={() => props.reroute(id, "edit")} />
          )}

        {props.crudMode === "edit" && (
          <SubmitButton
            onClick={() => save(props.template)}
            disabled={!props.unsavedChanges}
            label={saveEntriesCount > 1 ? `Save (${saveEntriesCount} entries)` : undefined}
          />
        )}
      </Stack>
    </Stack>
  );
};
